import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; }
}
function apiFetch(path, opts = {}) {
  const token = getToken();
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(token ? { 'x-worker-token': token } : {}), ...opts.headers },
  }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
}

const TYPE_COLORS = {
  'NVE / Power Company':  { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' },
  'Horizontal Drilling':  { bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6' },
  'Directional Drilling': { bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6' },
  'Vertical Drilling':    { bg: '#ede9fe', text: '#5b21b6', dot: '#8b5cf6' },
  'Shop / Trucking':      { bg: '#dcfce7', text: '#166534', dot: '#22c55e' },
  'General':              { bg: '#f3f4f6', text: '#374151', dot: '#6b7280' },
  'Loops':                { bg: '#fce7f3', text: '#9d174d', dot: '#ec4899' },
};

function timeAgo(ts) {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function ConversationCard({ conv, onClick }) {
  const colors = TYPE_COLORS[conv.name] || TYPE_COLORS['General'];
  return (
    <div
      onClick={() => onClick(conv.name)}
      style={{
        background: '#fff',
        border: '1.5px solid var(--border)',
        borderRadius: 14,
        padding: 20,
        cursor: 'pointer',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = colors.dot; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ background: colors.bg, color: colors.text, fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 99 }}>
          {conv.name}
        </span>
        {conv.latest_note && (
          <span style={{ fontSize: 11, color: 'var(--gray-muted)' }}>{timeAgo(conv.latest_note.created_at)}</span>
        )}
      </div>

      <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.55, minHeight: 40,
        display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {conv.summary || <span style={{ color: 'var(--gray-muted)', fontStyle: 'italic' }}>No summary yet — submit daily notes to generate one.</span>}
      </p>

      {conv.latest_note && (
        <div style={{ background: '#f9fafb', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#6b7280' }}>
          <span style={{ fontWeight: 600, color: '#374151' }}>
            {conv.latest_note.first_name} {conv.latest_note.last_name}
          </span>
          {conv.latest_note.project_name && (
            <span> · {conv.latest_note.project_name}</span>
          )}
          <p style={{ margin: '4px 0 0', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {conv.latest_note.note}
          </p>
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, marginTop: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: colors.dot }} />
          <span style={{ fontSize: 12, color: 'var(--gray-muted)' }}>
            {conv.project_count} project{conv.project_count !== 1 ? 's' : ''}
          </span>
        </div>
        <span style={{ fontSize: 12, color: 'var(--gray-muted)' }}>{conv.note_count} notes</span>
      </div>
    </div>
  );
}

function DetailDrawer({ typeName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);

  const colors = TYPE_COLORS[typeName] || TYPE_COLORS['General'];
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiFetch(`/conversations/${encodeURIComponent(typeName)}`),
      apiFetch(`/conversations/${encodeURIComponent(typeName)}/comments`),
    ]).then(([d, c]) => {
      setData(d);
      setSummaryDraft(d.summary || '');
      setComments(c || []);
    }).finally(() => setLoading(false));
  }, [typeName]);

  useEffect(() => { load(); }, [load]);

  async function saveSummary() {
    setSaving(true);
    try {
      await apiFetch(`/conversations/${encodeURIComponent(typeName)}/summary`, {
        method: 'POST',
        body: JSON.stringify({ summary: summaryDraft }),
      });
      setData(d => ({ ...d, summary: summaryDraft, edited_at: new Date().toISOString() }));
      setEditingSummary(false);
    } catch {}
    setSaving(false);
  }

  async function postComment() {
    if (!newComment.trim()) return;
    setPostingComment(true);
    try {
      const c = await apiFetch(`/conversations/${encodeURIComponent(typeName)}/comments`, {
        method: 'POST',
        body: JSON.stringify({ comment: newComment.trim() }),
      });
      setComments(prev => [...prev, c]);
      setNewComment('');
    } catch {}
    setPostingComment(false);
  }

  async function deleteComment(id) {
    if (!window.confirm('Delete this comment?')) return;
    await apiFetch(`/conversations/${encodeURIComponent(typeName)}/comments/${id}`, { method: 'DELETE' });
    setComments(prev => prev.filter(c => c.id !== id));
  }

  const q = search.toLowerCase();
  // Flatten all notes from all projects, sort newest first
  const allNotes = (data?.projects || [])
    .flatMap(p => p.notes.map(n => ({ ...n, project_name: p.name, project_status: p.status })))
    .filter(n => !q || n.note?.toLowerCase().includes(q) || `${n.first_name} ${n.last_name}`.toLowerCase().includes(q) || n.project_name?.toLowerCase().includes(q))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex' }}>
      {/* Backdrop */}
      <div style={{ flex: 1, background: 'rgba(0,0,0,0.4)' }} onClick={onClose} />

      {/* Drawer */}
      <div style={{ width: 600, maxWidth: '95vw', background: '#fff', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 32px rgba(0,0,0,0.15)' }}>
        {/* Sticky header */}
        <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ background: colors.bg, color: colors.text, fontSize: 13, fontWeight: 700, padding: '5px 12px', borderRadius: 99 }}>
              {typeName}
            </span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>✕</button>
          </div>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search notes…"
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13 }}
          />
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
          {loading && <div style={{ color: 'var(--gray-muted)', textAlign: 'center', padding: 40 }}>Loading…</div>}

          {!loading && (
            <>
              {/* Summary block */}
              <div style={{ background: '#f9fafb', borderRadius: 12, padding: 16, border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Summary {data?.edited_at ? `· Edited ${timeAgo(data.edited_at)} by ${data.edited_by}` : data?.generated_at ? `· Auto-generated ${timeAgo(data.generated_at)}` : ''}
                  </span>
                  {!editingSummary && (
                    <button onClick={() => setEditingSummary(true)}
                      style={{ fontSize: 12, color: colors.dot, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>
                      Edit
                    </button>
                  )}
                </div>
                {editingSummary ? (
                  <>
                    <textarea
                      value={summaryDraft}
                      onChange={e => setSummaryDraft(e.target.value)}
                      rows={5}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button onClick={() => setEditingSummary(false)}
                        style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid var(--border)', background: '#fff', fontSize: 13, cursor: 'pointer' }}>
                        Cancel
                      </button>
                      <button onClick={saveSummary} disabled={saving}
                        style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: colors.dot, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  <p style={{ margin: 0, fontSize: 13, color: data?.summary ? '#374151' : 'var(--gray-muted)', fontStyle: data?.summary ? 'normal' : 'italic', lineHeight: 1.6 }}>
                    {data?.summary || 'No summary yet. One will be auto-generated as workers submit daily notes.'}
                  </p>
                )}
              </div>

              {allNotes.length === 0 && (
                <div style={{ textAlign: 'center', color: 'var(--gray-muted)', padding: '32px 0', fontSize: 14 }}>
                  {search ? 'No notes match your search.' : 'No notes yet for this sub-job type.'}
                </div>
              )}

              {/* Flat reverse-chronological notes with project label */}
              {allNotes.map(note => (
                <div key={note.id} style={{ background: '#f9fafb', borderRadius: 10, padding: '12px 14px', borderLeft: `3px solid ${colors.dot}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, flexWrap: 'wrap', gap: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                      {note.first_name} {note.last_name}
                      {note.project_name && (
                        <span
                          onClick={e => { e.stopPropagation(); onClose(); navigate(`/admin/projects/${note.project_id}`); }}
                          style={{ fontWeight: 600, color: colors.dot, marginLeft: 6, cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                        >
                          · {note.project_name}
                        </span>
                      )}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--gray-muted)' }}>{formatDate(note.created_at)}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{note.note}</p>
                </div>
              ))}

              {/* Admin comments */}
              <div style={{ borderTop: '2px solid var(--border)', paddingTop: 20, marginTop: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14 }}>
                  Admin Comments
                </div>

                {comments.length === 0 && (
                  <p style={{ fontSize: 13, color: 'var(--gray-muted)', fontStyle: 'italic', marginBottom: 14 }}>No comments yet.</p>
                )}

                {comments.map(c => (
                  <div key={c.id} style={{ background: '#fff8f0', border: `1px solid ${colors.dot}30`, borderLeft: `3px solid ${colors.dot}`, borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{c.author}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                        <span style={{ fontSize: 11, color: 'var(--gray-muted)' }}>{formatDate(c.created_at)}</span>
                        <button onClick={() => deleteComment(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14, padding: 0, lineHeight: 1 }}>✕</button>
                      </div>
                    </div>
                    <p style={{ margin: '6px 0 0', fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{c.comment}</p>
                  </div>
                ))}

                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <textarea
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    placeholder="Add a comment…"
                    rows={2}
                    onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) postComment(); }}
                    style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, resize: 'none', fontFamily: 'inherit' }}
                  />
                  <button
                    onClick={postComment}
                    disabled={postingComment || !newComment.trim()}
                    style={{ padding: '0 16px', borderRadius: 8, border: 'none', background: newComment.trim() ? colors.dot : '#e5e7eb', color: '#fff', fontWeight: 700, fontSize: 13, cursor: newComment.trim() ? 'pointer' : 'default', alignSelf: 'stretch' }}
                  >
                    {postingComment ? '…' : 'Post'}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: 'var(--gray-muted)', marginTop: 5 }}>⌘↵ to post</div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminConversations() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    apiFetch('/conversations')
      .then(setConversations)
      .finally(() => setLoading(false));
  }, []);

  const q = search.toLowerCase();
  const filtered = conversations.filter(c =>
    !q ||
    c.name.toLowerCase().includes(q) ||
    c.summary?.toLowerCase().includes(q) ||
    c.latest_note?.note?.toLowerCase().includes(q) ||
    c.latest_note?.project_name?.toLowerCase().includes(q)
  );

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Conversations</h1>
          <p className="admin-page-sub">Field activity by sub-job type across all projects</p>
        </div>
      </div>

      <div className="admin-card" style={{ marginBottom: 20 }}>
        <div style={{ padding: '14px 20px' }}>
          <input
            className="field-input"
            placeholder="Search across all conversations…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box' }}
          />
        </div>
      </div>

      {loading && <div className="admin-empty">Loading…</div>}

      {!loading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {filtered.map(conv => (
            <ConversationCard key={conv.name} conv={conv} onClick={setSelected} />
          ))}
        </div>
      )}

      {selected && (
        <DetailDrawer typeName={selected} onClose={() => setSelected(null)} />
      )}
    </AdminLayout>
  );
}
